import axios from "axios";
import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Button from "../../../components/button";
import { courtAdd } from "../../../api/endpoint";
import toast from "react-hot-toast";
import Select from "react-select";
import { useSelector } from "react-redux";
import Input from "../../../components/input";
import Loader from "../../../components/loader";

const AddCourtModal = ({ isAddOpen, closeAddModal, fetchCourts }) => {
  const initialCourtState = {
    courtName: "",
    address: "",
    latitude: "",
    longitude: "",
    zipcode: "",
    sports: [],
    environment: [],
    access: "",
    phone: "",
    surfaceQuality: "",
    courtSize: "",
    numberOfIndoorCourt: "",
    numberOfOutdoorCourt: "",
    amenities: [],
  };
  const [newCourt, setNewCourt] = useState(initialCourtState);
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    courtName: "",
    address: "",
    zipcode: "",
    latitude: "",
    longitude: "",
    sports: "",
    environment: [],
    access: "",
    phone: "",
    surfaceQuality: "",
    courtSize: "",
    numberOfIndoorCourt: "",
    numberOfOutdoorCourt: "",
    amenities: "",
  });

  const { auth } = useSelector((state) => state.login);
  const { sports } = useSelector((state) => state.sports);

  useEffect(() => {
    const sportsData = sports?.map((sport) => ({
      value: sport?.id,
      label: sport?.sportName,
    }));
    setSportsList(sportsData);
  }, [sports]);

  const validateFields = () => {
    const newErrors = {};

    // Validate Court Name
    if (!newCourt.courtName.trim()) {
      newErrors.courtName = "Court name is required.";
    } else if (!/^[a-zA-Z\s]*$/.test(newCourt.courtName)) {
      newErrors.courtName = "Court name should only contain letters.";
    }

    // Validate Sports
    if (!newCourt.sports.length) {
      newErrors.sports = "At least one sport must be selected.";
    }

    if (newCourt?.numberOfIndoorCourt !== "" && newCourt?.numberOfIndoorCourt < 0) {
      newErrors.numberOfIndoorCourt = "Number of Courts cannot be a negative number";
    }

    if (newCourt?.numberOfOutdoorCourt !== "" && newCourt?.numberOfOutdoorCourt < 0) {
      newErrors.numberOfOutdoorCourt = "Number of Courts cannot be a negative number";
    }

    // Validate phone number allowing digits, dashes and ensuring 10 or 12 digits
    if (
      newCourt.phone &&
      (!/^[0-9\-]+$/.test(newCourt.phone) || // only digits and dashes
        !/^(\d{10,12})$/.test(newCourt.phone.replace(/-/g, ""))) // must be 10 to 12 digits ignoring dashes
    ) {
      newErrors.phone = "Phone number must be 10 to 12 digits and can include optional dashes.";
    }

    // Validate Access
    if (newCourt.access === undefined || newCourt.access === null || newCourt.access === "") {
      newErrors.access = "Access is required.";
    }

    // Validate Address + Zipcode OR Latitude + Longitude
    const hasAddress = newCourt.address.trim() !== "";
    const hasZipcode = newCourt.zipcode && newCourt.zipcode.trim() !== "";
    const hasLatitude = newCourt.latitude !== null && newCourt.latitude !== "";
    const hasLongitude = newCourt.longitude !== null && newCourt.longitude !== "";

    if ((hasAddress || hasZipcode) && !(hasAddress && hasZipcode)) {
      if (!hasAddress) newErrors.address = "Address is required if zipcode is provided.";
      if (!hasZipcode) newErrors.zipcode = "Zipcode is required if address is provided.";
    } else if ((hasLatitude || hasLongitude) && !(hasLatitude && hasLongitude)) {
      if (!hasLatitude) newErrors.latitude = "Latitude is required if longitude is provided.";
      if (!hasLongitude) newErrors.longitude = "Longitude is required if latitude is provided.";
    } else if (!(hasAddress && hasZipcode) && !(hasLatitude && hasLongitude)) {
      newErrors.address = "Either Address + Zipcode or Latitude + Longitude are required.";
      newErrors.latitude = "Either Address + Zipcode or Latitude + Longitude are required.";
    }

    // Update errors state
    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return; // Stop submission if validation fails

    const courtToSubmit = [
      {
        ...newCourt,
        numberOfIndoorCourt: newCourt?.numberOfIndoorCourt === "" ? null : Number(newCourt?.numberOfIndoorCourt),
        numberOfOutdoorCourt: newCourt?.numberOfOutdoorCourt === "" ? null : Number(newCourt?.numberOfOutdoorCourt),
        environment: newCourt?.environment?.map((env) => env?.value),
        amenities: newCourt.amenities.map((amenity) => amenity.value),
        sports: newCourt.sports.map((sport) => sport.value),
      },
    ];
    try {
      setLoading(true);
      const response = await axios.post(courtAdd, courtToSubmit, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth}`,
        },
      });
      toast.success("Court submitted successfully!");
      handleReset();
      fetchCourts();
      handleClose();
    } catch (error) {
      const errorMessage = error.response?.data?.user_description || "An unexpected error occurred.";
      toast.error(errorMessage);
      console.error("Error submitting court:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setNewCourt(initialCourtState);
    setErrors({
      courtName: "",
      address: "",
      zipcode: "",
      latitude: "",
      longitude: "",
      sports: [],
      environment: [],
      access: "",
      phone: "",
      surfaceQuality: "",
      courtSize: "",
      numberOfCourts: "",
      amenities: "",
    });
  };

  const handleClose = () => {
    handleReset();
    closeAddModal();
  };

  return (
    <>
      <Modal show={isAddOpen} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Court</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Input
                label="Court Name"
                type="text"
                id="courtName"
                placeholder="Court Name"
                value={newCourt.courtName}
                onChange={(e) => setNewCourt({ ...newCourt, courtName: e.target.value })}
                required={true}
              />
              {errors?.courtName && <div className="form-error-message">{errors?.courtName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                label="Address"
                type="textarea"
                id="address"
                placeholder="Address"
                value={newCourt.address}
                onChange={(e) => setNewCourt({ ...newCourt, address: e.target.value })}
                // required={true}
                textareaRows={1}
              />
              {errors?.address && <div className="form-error-message">{errors?.address}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Input
                label="Latitude"
                type="text"
                placeholder="Latitude"
                value={newCourt.latitude}
                onChange={(e) => setNewCourt({ ...newCourt, latitude: e.target.value })}
              />
              {errors?.latitude && <div className="form-error-message">{errors?.latitude}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                label="Longitude"
                type="text"
                placeholder="Longitude"
                value={newCourt.longitude}
                onChange={(e) => setNewCourt({ ...newCourt, longitude: e.target.value })}
              />
              {errors?.longitude && <div className="form-error-message">{errors?.longitude}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="sportsID" className="form-label">
                Sport
              </label>
              <Select
                id="sportsID"
                value={newCourt.sports}
                onChange={(selectedOptions) => setNewCourt({ ...newCourt, sports: selectedOptions })}
                options={sportsList}
                isMulti={true}
                closeMenuOnSelect={false}
                required={true}
              />
              {errors?.sports && <div className="form-error-message">{errors?.sports}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                type="select"
                label="Access"
                value={newCourt.access}
                onChange={(e) => setNewCourt({ ...newCourt, access: e.target.value })}
                options={[
                  { value: "", label: "Select Access" },
                  { value: true, label: "Public" },
                  { value: false, label: "Private" },
                ]}
              />
              {errors?.access && <div className="form-error-message">{errors?.access}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Input
                label="Number of Indoor Courts"
                type="text"
                placeholder="Number of Indoor Courts"
                id="numberOfIndoorCourt"
                value={newCourt.numberOfIndoorCourt}
                onChange={(e) => {
                  setNewCourt({
                    ...newCourt,
                    numberOfIndoorCourt: e.target.value,
                  });
                }}
                required={true}
              />
              {errors?.numberOfIndoorCourt && <div className="form-error-message">{errors?.numberOfIndoorCourt}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                label="Number of Outdoor Courts"
                type="text"
                placeholder="Number of Outdoor Courts"
                id="numberOfOutdoorCourt"
                value={newCourt.numberOfOutdoorCourt}
                onChange={(e) => {
                  setNewCourt({
                    ...newCourt,
                    numberOfOutdoorCourt: e.target.value,
                  });
                }}
                required={true}
              />
              {errors?.numberOfOutdoorCourt && <div className="form-error-message">{errors?.numberOfOutdoorCourt}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="amenities" className="form-label">
                Environment
              </label>
              <Select
                isMulti
                value={newCourt.environment}
                onChange={(selectedOptions) => setNewCourt({ ...newCourt, environment: selectedOptions })}
                options={[
                  { value: "INDOOR", label: "Indoor" },
                  { value: "OUTDOOR", label: "Outdoor" },
                ]}
              />
              {/* <Input
                label="Enviroment"
                type="select"
                value={newCourt.environment}
                onChange={(e) =>
                  setNewCourt({ ...newCourt, environment: e.target.value })
                }
                options={[
                  { value: "", label: "Select Environment" },
                  { value: "INDOOR", label: "Indoor" },
                  { value: "OUTDOOR", label: "Outdoor" },
                  { value: "BOTH", label: "Both" },
                ]}
              /> */}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                label="Surface Quality"
                type="text"
                id="surfaceQuality"
                placeholder="Surface Quality"
                value={newCourt.surfaceQuality}
                onChange={(e) => setNewCourt({ ...newCourt, surfaceQuality: e.target.value })}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Input
                label="Court Size"
                type="text"
                id="courtSize"
                placeholder="Court Size"
                value={newCourt.courtSize}
                onChange={(e) => setNewCourt({ ...newCourt, courtSize: e.target.value })}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="amenities">Amenities</label>
              <Select
                id="amenities"
                isMulti
                value={newCourt.amenities}
                onChange={(selectedOptions) => setNewCourt({ ...newCourt, amenities: selectedOptions })}
                options={[
                  { value: "Lighted Courts", label: "Lighted Courts" },
                  { label: "Seating/Benches", value: "Seating/Benches" },
                  { label: "Scoreboards", value: "Scoreboards" },
                  { label: "Parking", value: "Parking" },
                  { label: "Court Dividers", value: "Court Dividers" },
                  { label: "Locker Rooms", value: "Locker Rooms" },
                  { label: "Equipment Rentals", value: "Equipment Rentals" },
                  { label: "Hydration Stations", value: "Hydration Stations" },
                ]}
                className="mt-1"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <Input
                label="Phone"
                type="text"
                id="phone"
                placeholder="Phone"
                value={newCourt.phone}
                onChange={(e) => setNewCourt({ ...newCourt, phone: e.target.value })}
              />
              {errors?.phone && <div className="form-error-message">{errors?.phone}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <Input
                label="Zip Code"
                type="text"
                className="form-control"
                id="zipcode"
                placeholder="Zip Code"
                value={newCourt.zipcode}
                onChange={(e) => setNewCourt({ ...newCourt, zipcode: e.target.value })}
                // required={true}
              />
              {errors?.zipcode && <div className="form-error-message">{errors?.zipcode}</div>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button text="Reset" callBack={() => handleReset()} />
            <Button
              text="Add Court"
              callBack={() => handleSubmit()}
              style={{ backgroundColor: "#2c3e50", color: "#ecf0f1" }}
            />
          </div>
        </Modal.Footer>
      </Modal>
      {loading && <Loader />}
    </>
  );
};

export default AddCourtModal;
